import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getPendingDealDoc, PENDING_DEAL_EVENT } from '../services/branch';
import { Deal, dealFromMap } from '../types/deal';
import { useFirebaseImageUrl } from '../hooks/useFirebaseImageUrl';
import { getPageImage, getPageLayout, layoutToStyle } from '../utils/dealImages';
import { localizeDeal } from '../utils/dealLocale';
import { useAwaitingDeal } from '../hooks/useAwaitingDeal';
import { redeemPromoCode } from '../services/promoCode';
import { trackWebCodeRedemption, trackStoreLinkClick } from '../services/unifiedAnalytics';
import { buildAppMagicLink } from '../services/appLink';
import { sendAppDownloadLink } from '../services/emailVerification';
import { getCustomerInfo, hasActiveSubscription } from '../services/revenuecat';
import {
  trackPromoCodeSubmitted,
  trackPromoCodeRedeemedSuccess,
  trackPromoCodeRedeemedFailed,
} from '../services/analytics';
import CoBrandHeader from './CoBrandHeader';
import CalloutCard from './CalloutCard';
import LanguageSwitcher from './LanguageSwitcher';
import LoadingRive from './LoadingRive';
import { t } from '../i18n';
import './Auth.css';
import './Promo.css';


const CodeRedemption: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, userDocData, logout } = useAuth();
  const [activeDeal, setActiveDeal] = useState<Deal | null>(null);
  const [code, setCode] = useState('');
  const [redeeming, setRedeeming] = useState(false);
  const [validation, setValidation] = useState<{ success: boolean; message: string } | null>(null);
  const [redeemed, setRedeemed] = useState((location.state as any)?.redeemed === true);
  const [magicLink, setMagicLink] = useState('');
  const formImage = useFirebaseImageUrl(getPageImage(activeDeal, 'redeem'));
  const successImage = useFirebaseImageUrl(getPageImage(activeDeal, 'success'));
  const [formLoaded, setFormLoaded] = useState(false);
  const [successLoaded, setSuccessLoaded] = useState(false);
  const awaitingForm = useAwaitingDeal(formImage, formLoaded);
  const awaitingSuccess = useAwaitingDeal(successImage, successLoaded);
  const localizedDeal = localizeDeal(activeDeal);

  useEffect(() => {
    const sync = () => {
      if (userDocData?.settings?.active_deal) {
        setActiveDeal(dealFromMap(userDocData.settings.active_deal));
        return;
      }
      const pending = getPendingDealDoc();
      if (pending) setActiveDeal(pending);
    };
    sync();
    window.addEventListener(PENDING_DEAL_EVENT, sync);
    return () => window.removeEventListener(PENDING_DEAL_EVENT, sync);
  }, [userDocData]);

  // Block users who already have an active subscription (skip the just-redeemed success view)
  useEffect(() => {
    if (!currentUser || redeemed) return;
    let cancelled = false;
    (async () => {
      try {
        const subscriber = await getCustomerInfo(currentUser.uid);
        if (!cancelled && hasActiveSubscription(subscriber)) {
          navigate('/subscription');
        }
      } catch (err) {
        console.error('[Redeem] Subscription check failed:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [currentUser, redeemed, navigate]);

  // Ensure the download CTA has a link even when arriving already-redeemed.
  useEffect(() => {
    if (redeemed && !magicLink && currentUser) {
      buildAppMagicLink(currentUser.email).then(setMagicLink);
    }
  }, [redeemed, magicLink, currentUser]);

  const handleRedeem = async () => {
    if (!code.trim()) {
      setValidation({ success: false, message: t('offers.enterPromoCodeError') });
      return;
    }
    if (!currentUser) {
      setValidation({ success: false, message: t('offers.loginToRedeem') });
      return;
    }

    setRedeeming(true);
    setValidation(null);

    try {
      const result = await redeemPromoCode(code, currentUser.uid);

      if (result.success) {
        trackPromoCodeSubmitted(code.toUpperCase(), currentUser.email || undefined, currentUser.uid, true);
        trackPromoCodeRedeemedSuccess(code.toUpperCase(), currentUser.email || undefined, currentUser.uid);
        trackWebCodeRedemption(activeDeal?.id, code.toUpperCase(), currentUser.email || undefined, currentUser.uid);

        // Wait for backend to process and RevenueCat to sync
        await new Promise((resolve) => setTimeout(resolve, 3000));

        const subscriber = await getCustomerInfo(currentUser.uid);
        if (hasActiveSubscription(subscriber)) {
          // Build the app magic link and email it to the user, then show success.
          const link = await buildAppMagicLink(currentUser.email);
          setMagicLink(link);
          setRedeemed(true);
          if (currentUser.email) {
            sendAppDownloadLink(currentUser.email, link).catch((e) =>
              console.error('[Redeem] Failed to send app download link:', e)
            );
          }
        } else {
          setValidation({ success: false, message: t('offers.promoRedeemButNotActive') });
        }
      } else {
        const message = result.error || t('offers.invalidCouponCode');
        setValidation({ success: false, message });
        trackPromoCodeSubmitted(code.toUpperCase(), currentUser.email || undefined, currentUser.uid, false, result.error);
        trackPromoCodeRedeemedFailed(code.toUpperCase(), message, currentUser.email || undefined, currentUser.uid);
      }
    } catch (err: any) {
      console.error('Error redeeming code:', err);
      setValidation({ success: false, message: t('offers.unableToRedeemPromo') });
      trackPromoCodeSubmitted(code.toUpperCase(), currentUser.email || undefined, currentUser.uid, false, err.message || 'Unknown error');
      trackPromoCodeRedeemedFailed(code.toUpperCase(), err.message || 'Unknown error', currentUser.email || undefined, currentUser.uid);
    } finally {
      setRedeeming(false);
    }
  };

  const handleUseDifferentAccount = async () => {
    await logout();
    navigate('/login');
  };

  const brandHeader = <CoBrandHeader deal={activeDeal} />;

  if (redeemed) {
    return (
      <div className="hp-page">
        <LanguageSwitcher />
        <div className="hp-split">
          <div className={`hp-hero${awaitingSuccess ? ' hp-hero--awaiting' : ''}`}>
            {successImage && (
              <img src={successImage} alt={activeDeal?.name || ''} className="hp-hero-fill" style={layoutToStyle(getPageLayout(activeDeal, 'success'))} onLoad={() => setSuccessLoaded(true)} />
            )}
          </div>

          <div className="hp-panel">
            <div className="hp-panel-inner">
              {brandHeader}

              <CalloutCard deal={localizedDeal} page="success" />

              <div className="hp-success">
                <p className="hp-success-lead">{t('promo.successLead')}</p>
                <div className="hp-success-card">
                  <span>{t('promo.successTitle')}</span>
                </div>
                <p className="hp-success-sub">
                  {t('promo.redeemSuccessSub')}
                </p>
              </div>

              <a
                className="hp-cta"
                href={magicLink || '#'}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => {
                  if (!magicLink) { e.preventDefault(); return; }
                  trackStoreLinkClick('redeem_success', activeDeal?.id, currentUser?.uid);
                }}
              >
                {t('promo.downloadAppCta')}
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="hp-page">
      <LanguageSwitcher />
      <div className="hp-split">
        <div className={`hp-hero${awaitingForm ? ' hp-hero--awaiting' : ''}`}>
          {formImage && (
            <img src={formImage} alt={activeDeal?.name || ''} className="hp-hero-fill" style={layoutToStyle(getPageLayout(activeDeal, 'redeem'))} onLoad={() => setFormLoaded(true)} />
          )}
        </div>

        <div className="hp-panel">
          <div className="hp-panel-inner">
            {brandHeader}

            <CalloutCard deal={localizedDeal} page="redeem" />

            <div className="hp-heading">
              <h1 className="hp-headline">{t('promo.redeemHeadline')}</h1>
              <p className="hp-subtitle">
                {localizedDeal?.message || t('promo.redeemSubtitle')}
              </p>
            </div>

            <form
              className="auth-form"
              onSubmit={(e) => {
                e.preventDefault();
                handleRedeem();
              }}
            >
              <div className="input-field">
                <input
                  id="redeem-code"
                  type="text"
                  value={code}
                  onChange={(e) => {
                    setCode(e.target.value.toUpperCase());
                    setValidation(null);
                  }}
                  required
                  placeholder={t('offers.enterPromoCode')}
                  disabled={redeeming}
                />
              </div>

              {validation && !validation.success && (
                <div className="error-message">{validation.message}</div>
              )}

              <button type="submit" disabled={redeeming || !code.trim()} className="hp-cta">
                {redeeming ? t('offers.redeeming') : t('offers.redeem')}
              </button>
            </form>

            <p className="hp-secondary-link">
              <strong>{currentUser?.email}</strong>{' — '}
              <button onClick={handleUseDifferentAccount}>{t('verifyEmail.useDifferentAccount')}</button>
            </p>
          </div>
        </div>
      </div>

      {redeeming && (
        <div className="redemption-overlay">
          <div className="redemption-loader">
            <LoadingRive animation="LogoFillingLoader" label={t('offers.redeemingPromo')} />
          </div>
        </div>
      )}
    </div>
  );
};

export default CodeRedemption;
